import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from "../../auth/SessionContext";
import { DEBUG } from '../..';
import { steamLoginUrl } from "../../auth/steamLogin";

const CartContext = createContext();

const STORAGE_KEY = 'lmutvCartSelection';

const defaultSelection = { edition: 'standard', duration: 'lifetime', pcs: 1 };

const isISODate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const readSelection = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultSelection;
        const parsed = JSON.parse(raw);
        return {
            edition: parsed.edition === 'pro' ? 'pro' : 'standard',
            duration: ['3m', '1y', 'lifetime'].includes(parsed.duration) ? parsed.duration : 'lifetime',
            pcs: Math.max(1, parseInt(parsed.pcs, 10) || 1),
            startsAt: isISODate(parsed.startsAt) ? parsed.startsAt : undefined,
            autoAdd: parsed.autoAdd === true,
        };
    } catch (e) {
        return defaultSelection;
    }
};

export const CartProvider = ({ children }) => {
    const { sessionData, loadSessionDataAsync } = useSession();
    const navigate = useNavigate();
    const [opened, setOpened] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(readSelection());

    const handleSteamLogin = async (selection) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
            window.location.href = steamLoginUrl(true);
        } catch (error) {
            if (DEBUG) console.error("Error initiating Steam login:", error);
        }
    };

    const openCart = async (opts) => {
        const selection = opts ? { ...defaultSelection, autoAdd: false, ...opts } : readSelection();
        if (DEBUG) console.log("openCart selection", selection);
        // Subscriptions (3m/1y) → recurring checkout. Only lifetime uses the one-time
        // cart below (PayPal can't mix subscription + capture in one transaction).
        if (selection.duration === '3m' || selection.duration === '1y') {
            const q = new URLSearchParams({
                edition: selection.edition,
                duration: selection.duration,
                pcs: String(selection.pcs || 1),
            });
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...selection, autoAdd: false }));
            navigate(`/subscribe?${q.toString()}`);
            return;
        }
        setPendingSelection(selection);
        // Persist only the non-transient bits — autoAdd must not survive a reload,
        // otherwise a later plain "Buy" click would silently re-seed the cart.
        const { autoAdd, ...persisted } = selection;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        const loggedIn = await loadSessionDataAsync();
        return loggedIn ? setOpened(true) : handleSteamLogin(selection);
    };

    const closeCart = () => setOpened(false);

    useEffect(() => {
        if (DEBUG) console.log("opened cart ", opened);
    }, [opened]);

    return (
        <CartContext.Provider value={{ opened, openCart, closeCart, pendingSelection }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
